import React, { FC, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Typography, TypographyTagVariants } from '@components/atoms/Typography';
import { Form } from '@components/atoms/Form';
import { RoundProfilePicture } from '@components/atoms/RoundProfilePicture';
import { Space } from '@components/atoms/Space';
import styles from './TwoFactorAuth.module.css';
import { Button, ButtonMode } from '@components/atoms/Button';
import { useData } from '@hooks/useData';
import { useNotifications } from '@hooks/useNotifications';
import { useGoogle2FAQrCode } from '@services/useGoogle2FAQrCode';
import { useGoogle2FAVerify } from '@services/useGoogle2FAVerify';
import { OTPInput } from '@components/atoms/OTPInput/OTPInput';

export const TwoFactorAuth: FC = () => {
    const { t } = useTranslation();
    const { showDefaultNotification } = useNotifications();

    const translations = {
        activate: t('2fa.activate'),
        label: t('2fa.label'),
        hint: t('2fa.hint'),
        openApp: t('2fa.open-app'),
        verify: t('2fa.verify'),
        enterCode: t('2fa.enter-code'),
        manual: t('2fa.manual'),
        manualKey: t('2fa.manual-key'),
        activated: t('2fa.activated'),
        inactive: t('2fa.inactive'),
    };

    const { user, qrCode, setUser } = useData();
    const { request: requestQrCode, loading: qrLoading, error: qrError, lastResponseTime: qrResponseTime } = useGoogle2FAQrCode();
    const {
        request: requestVerify,
        loading: verifyLoading,
        error: verifyError,
        success: verifySuccess,
        lastResponseTime: verifyResponseTime,
    } = useGoogle2FAVerify();

    const formRef = useRef<HTMLFormElement>(null);
    const pickerRef = useRef<HTMLDivElement>(null);
    const [pickerOpen, setPickerOpen] = useState(false);
    const [code, setCode] = useState('');
    const [generated, setGenerated] = useState(false);

    const handleOnClick = () => {
        setPickerOpen(true);
    };

    const handleGenerate = () => {
        const email = user?.data.email;
        if (!email) return;

        requestQrCode({ email });
        formRef?.current?.requestSubmit();
        setGenerated(true);
    };

    const handleVerify = () => {
        const data = { email: user?.data.email, secret: code };
        requestVerify(data);
        formRef?.current?.requestSubmit();
    };

    const submitForm = async () => {
        // intentionally empty
    };

    useEffect(() => {
        if (!pickerOpen) return;
        const handleClickOutside = (event: MouseEvent) => {
            if (pickerRef.current && !pickerRef.current.contains(event.target as Node)) {
                setPickerOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [pickerOpen]);

    useEffect(() => {
        if (!qrResponseTime) return;

        const url = qrCode?.data.otpauth_url;

        if (url) {
            try {
                window.location.href = url; // open authenticator app
            } catch {
                showDefaultNotification({ type: 'success', message: translations.manual });
            }
        }

        if (qrError) {
            showDefaultNotification({ type: 'error', message: qrError });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [qrResponseTime]);

    useEffect(() => {
        if (!verifyResponseTime) return;

        if (verifySuccess) {
            showDefaultNotification({ type: 'success', message: verifySuccess });
            setPickerOpen(false);
            setUser({
                ...user,
                data: {
                    ...user!.data,
                    google2fa_is_active: true,
                },
            });
        } else if (verifyError) {
            showDefaultNotification({ type: 'error', message: verifyError });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [verifyResponseTime]);

    return (
        <Form formRef={formRef} classes="d-flex flex-column" onSubmit={submitForm}>
            <Space spaceBreakpoints={{ xs: { space: 30 } }} />
            <span
                className="d-flex justify-content-between align-items-center"
                onClick={() => {
                    if (!user?.data.google2fa_is_active) handleOnClick();
                }}
                style={{
                    cursor: user?.data.google2fa_is_active ? 'default' : 'pointer',
                }}>
                <Typography fontSize="16" fontWeight="300">
                    <>
                        <Typography fontWeight="300" tagVariant={TypographyTagVariants.span}>
                            <i style={{ marginRight: '5px' }} className="fa-thin fa-shield-keyhole"></i>
                        </Typography>
                        {` ${translations.activate}`}
                    </>
                </Typography>

                <span className="d-flex align-items-center" style={{ gap: '6px' }}>
                    <Typography color={user?.data.google2fa_is_active ? 'success' : 'danger'} fontWeight="800">
                        {user?.data.google2fa_is_active ? ` ${translations.activated}` : ` ${translations.inactive}`}
                    </Typography>
                </span>
            </span>

            <>
                {pickerOpen && (
                    <div className={styles.overlay}>
                        <div className={styles.pickerContainer} ref={pickerRef}>
                            <RoundProfilePicture
                                icon="fa-thin fa-shield-keyhole"
                                labelText={translations.label}
                                hintText={translations.hint}
                            />
                            <Space spaceBreakpoints={{ xs: { space: 30 } }} />

                            {!generated && (
                                <Button
                                    label={translations.openApp}
                                    mode={ButtonMode.primary}
                                    classes={styles.button}
                                    disabled={qrLoading}
                                    onClick={handleGenerate}
                                />
                            )}

                            {generated && (
                                <>
                                    <Space spaceBreakpoints={{ xs: { space: 15 } }} />

                                    <OTPInput value={code} onChange={(val) => setCode(val)} />

                                    <Space spaceBreakpoints={{ xs: { space: 30 } }} />

                                    <Button
                                        label={translations.verify}
                                        mode={ButtonMode.primary}
                                        classes={styles.button}
                                        disabled={verifyLoading || code.length < 6}
                                        onClick={handleVerify}
                                    />

                                    {qrCode && (
                                        <>
                                            <Space spaceBreakpoints={{ xs: { space: 15 } }} />
                                            <Typography align="center">
                                                <span>
                                                    {translations.manualKey} {qrCode.data.secret}
                                                </span>
                                            </Typography>
                                        </>
                                    )}
                                </>
                            )}
                        </div>
                    </div>
                )}
            </>
        </Form>
    );
};
